import { useState, useEffect } from 'react';

interface UseIOSInstallReturn {
  isIOS: boolean;
  isStandalone: boolean;
  isInstallable: boolean;
  showPrompt: boolean;
  dismissPrompt: () => void;
  markAsInstalled: () => void;
}

export const useIOSInstall = (): UseIOSInstallReturn => {
  const [showPrompt, setShowPrompt] = useState(false);

  // Detect iOS
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  // Check if running in standalone mode (installed)
  const isStandalone = window.navigator.standalone === true;

  // Check if the app is installable (iOS, Safari, not already installed)
  const isInstallable =
    isIOS && !isStandalone && navigator.userAgent.includes('Safari');

  useEffect(() => {
    // Only show prompt on iOS Safari if not already installed
    if (isInstallable) {
      // Check if user has already dismissed the prompt
      const hasSeenPrompt = localStorage.getItem(
        'ios-install-prompt-dismissed'
      );
      const lastDismissed = hasSeenPrompt ? parseInt(hasSeenPrompt) : 0;
      const now = Date.now();
      const oneWeek = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

      // Show prompt if user hasn't seen it or it's been more than a week
      if (!hasSeenPrompt || now - lastDismissed > oneWeek) {
        // Delay showing the prompt to let the user explore the app first
        const timer = setTimeout(() => {
          setShowPrompt(true);
        }, 10000); // Show after 10 seconds

        return () => clearTimeout(timer);
      }
    }
  }, [isInstallable]);

  const dismissPrompt = () => {
    setShowPrompt(false);
    localStorage.setItem('ios-install-prompt-dismissed', Date.now().toString());
  };

  const markAsInstalled = () => {
    setShowPrompt(false);
    localStorage.setItem('ios-install-prompt-dismissed', Date.now().toString());
  };

  return {
    isIOS,
    isStandalone,
    isInstallable,
    showPrompt,
    dismissPrompt,
    markAsInstalled,
  };
};
